/**
 * Two-hand wing-stroke coincidence detector behind the flap jump and `createFlapDetector` (api.ts),
 * plus the pure bird-flap math (which part of a hand's motion is a wing stroke, and which way the
 * two strokes push the body). Pure (time is passed in), so tests drive it directly. Kept in its own
 * file so api.ts and flapJump.ts can both use it without a load-order dependency between them.
 */
import { isWorldUp, verticalOf } from './upMath';
import type { Vector3 } from 'three';

export interface FlapParams {
  /** Hand stroke speed (m/s, positive) that counts as a wing stroke. */
  threshold: number;
  /** Max seconds between the two hands' strokes. */
  window: number;
  /** Seconds after a flap before the next one can register. */
  cooldown: number;
}

/**
 * The wing-stroke part of a hand's rig-relative velocity, written to `out`; returns its length.
 *
 * A bird pushes air with the down/back/side stroke and folds its wings to come back, so the two
 * RECOVERY motions push nothing: raising the hand (+y) and reaching forward along the heading
 * (`forwardH`, unit and horizontal). Without that, lifting the arms before a flap would push the
 * body down and reaching forward would brake it. Sideways and backward sweeps and downstrokes stay.
 */
export function flapStroke(handRelVel: Vector3, forwardH: Vector3, out: Vector3, up?: Vector3): number {
  out.copy(handRelVel);
  if (up && !isWorldUp(up)) {
    // Planet: the raised-hand recovery is the positive part along up; reach is read on the tangent plane.
    const d = verticalOf(out, up);
    if (d > 0) out.addScaledVector(up, -d);
    const reach = out.dot(forwardH);
    if (reach > 0) out.addScaledVector(forwardH, -reach);
    return out.length();
  }
  if (out.y > 0) out.y = 0;
  const reach = out.x * forwardH.x + out.z * forwardH.z;
  if (reach > 0) {
    out.x -= forwardH.x * reach;
    out.z -= forwardH.z * reach;
  }
  return out.length();
}

/**
 * Bird flap direction (unit, into `out`): OPPOSITE to the average of both hands' strokes (swept
 * back → forward, swept left → right, swept down → up). The stronger hand weighs more. `lift` (0..1)
 * is the minimum upward share: a flap always lifts at least that much, so a backward sweep in the
 * air still slows the fall instead of only pushing forward. Hands that cancel out (one swept left,
 * the other right) leave a straight-up flap.
 */
export function flapDirection(left: Vector3, right: Vector3, lift: number, out: Vector3, up?: Vector3): Vector3 {
  out.copy(left).add(right).multiplyScalar(-0.5);
  const len = out.length();
  if (len < 1e-6) return up && !isWorldUp(up) ? out.copy(up) : out.set(0, 1, 0);
  out.divideScalar(len);
  if (up && !isWorldUp(up)) {
    const minUpP = Math.min(1, Math.max(0, lift));
    const d = verticalOf(out, up);
    if (d >= minUpP) return out;
    // Tangent part of the direction (reuse `out`: out = up*d + tan).
    const h = Math.sqrt(Math.max(0, 1 - d * d));
    if (h < 1e-6 || minUpP >= 1) return out.copy(up);
    const k = Math.sqrt(1 - minUpP * minUpP) / h;
    return out.addScaledVector(up, -d).multiplyScalar(k).addScaledVector(up, minUpP);
  }
  const minUp = Math.min(1, Math.max(0, lift));
  if (out.y >= minUp) return out;
  const h = Math.hypot(out.x, out.z);
  if (h < 1e-6 || minUp >= 1) return out.set(0, 1, 0);
  // Keep the horizontal heading, raise the upward share to `minUp`, stay a unit vector.
  const k = Math.sqrt(1 - minUp * minUp) / h;
  return out.set(out.x * k, minUp, out.z * k);
}

/**
 * Two-hand stroke coincidence detector. Pure (time is passed in), so tests drive it directly.
 *
 * Each hand remembers the last time it was in a stroke (updated every frame while it is). A flap
 * fires on the frame the second hand strokes when the other stroked at most `window` seconds
 * earlier. After a flap each hand must leave its stroke before it can count again, so one long
 * swing never produces a burst of flaps.
 */
export class FlapStrokeDetector {
  private readonly lastStroke: [number, number] = [Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY];
  private readonly armed: [boolean, boolean] = [true, true];
  private readyAt = Number.NEGATIVE_INFINITY;

  /** Feed one frame. `leftVy`/`rightVy` = rig-relative vertical hand speeds (m/s, +up): downstrokes only. */
  update(time: number, leftVy: number, rightVy: number, p: FlapParams): boolean {
    return this.stroke(time, -leftVy, -rightVy, p);
  }

  /**
   * Feed one frame with each hand's stroke speed (m/s, ≥ 0, e.g. the length `flapStroke` returns):
   * any direction counts, which is how the bird flap reads backward and sideways sweeps too.
   */
  stroke(time: number, left: number, right: number, p: FlapParams): boolean {
    this.track(0, left, time, p.threshold);
    this.track(1, right, time, p.threshold);
    if (time < this.readyAt) return false;
    const a = this.lastStroke[0];
    const b = this.lastStroke[1];
    if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
    // At least one hand strokes NOW, and the other did so within the window.
    if (Math.max(a, b) < time - 1e-9 || Math.abs(a - b) > p.window + 1e-9) return false;
    this.readyAt = time + p.cooldown;
    this.lastStroke[0] = this.lastStroke[1] = Number.NEGATIVE_INFINITY;
    this.armed[0] = this.armed[1] = false;
    return true;
  }

  reset(): void {
    this.lastStroke[0] = this.lastStroke[1] = Number.NEGATIVE_INFINITY;
    this.armed[0] = this.armed[1] = true;
    this.readyAt = Number.NEGATIVE_INFINITY;
  }

  /**
   * Forget pending strokes and require each hand to leave its stroke before it can count again.
   * Called while a hand pushes on a surface: a Gorilla push moves the hands down relative to the
   * rig just like a flap, and that motion must not turn into a flap once the body is airborne.
   */
  disarm(): void {
    this.lastStroke[0] = this.lastStroke[1] = Number.NEGATIVE_INFINITY;
    this.armed[0] = this.armed[1] = false;
  }

  private track(i: 0 | 1, speed: number, time: number, threshold: number): void {
    const stroking = speed > threshold;
    if (!stroking) this.armed[i] = true;
    else if (this.armed[i]) this.lastStroke[i] = time;
  }
}
